import {
  ChevronDownIcon,
  ChevronRightIcon,
  CloseIcon,
  HamburgerIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Collapse,
  Flex,
  Icon,
  IconButton,
  Image,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { firebaseLogout } from "../../config/firebase";
import { useAuthentication, useDeleteUser } from "../../hooks";
import useGetUser from "../../hooks/users/useGetUser";
import DeleteAlertDialog from "../Modals/DeleteAlertDialog";

export default function Navbar() {
  const { isOpen, onToggle } = useDisclosure();
  const router = useRouter();

  return (
    <Box>
      <Flex
        bg={useColorModeValue("white", "gray.800")}
        color={useColorModeValue("gray.600", "white")}
        minH={"60px"}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={"solid"}
        borderColor={useColorModeValue("gray.200", "gray.900")}
        align={"center"}
      >
        <Flex ml={{ base: -2 }} display={{ base: "flex", lg: "none" }}>
          <IconButton
            onClick={() => {
              onToggle();
              localStorage.removeItem("redirectSlug");
            }}
            icon={
              isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
            }
            variant={"ghost"}
            aria-label={"Toggle Navigation"}
          />
        </Flex>
        <Flex
          align={"center"}
          gap={"10px"}
          flex={{ base: 1 }}
          justify={{ base: "center", lg: "start" }}
          //TODO: Use a proper link here.
        >
          <Image
            src="/MainLogo.svg"
            alt="Cooking Logo"
            width={10}
            height={10}
            //TODO: Use a proper link here.
            onClick={() => router.push("/recipes")}
          />
          <Text
            textAlign={useBreakpointValue({ base: "center", md: "left" })}
            fontFamily={"heading"}
            color={useColorModeValue("gray.800", "white")}
            fontSize="xl"
            fontWeight="bold"
          >
            Recipe Log
          </Text>

          <Flex display={{ base: "none", lg: "flex" }} ml={10}>
            <DesktopNav />
          </Flex>
        </Flex>
      </Flex>
      {/*Make the mobile side-menu take up 100% of the screen, so it doesn't just push the content down.  */}
      <Collapse
        in={isOpen}
        animateOpacity
        style={{ position: "fixed", width: "100%", zIndex: "5000" }}
      >
        <MobileNav onToggle={onToggle} />
      </Collapse>
    </Box>
  );
}

type Props = {
  navItem: NavItem;
};

const DesktopNavComponent = ({ navItem }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const linkColor = useColorModeValue("gray.600", "gray.200");
  const activeLinkColor = useColorModeValue("brand.500", "brand.500");
  const router = useRouter();

  const linkHoverColor = useColorModeValue("gray.800", "white");
  const popoverContentBgColor = useColorModeValue("white", "gray.800");

  const handleCancelDelete = () => console.log("cancel");

  const handleSubmitDelete = () => console.log("ahhhhhhhhh deleted");

  return (
    <Box zIndex={50000} key={navItem.label}>
      <Popover trigger={"hover"} placement={"bottom-start"}>
        <PopoverTrigger>
          <Link
          onClick={() => {              localStorage.removeItem("redirectSlug");
        }}
            as={NextLink}
            _active={{ color: "red" }}
            _activeLink={{ color: "red" }}
            p={2}
            href={navItem.href ?? "#"}
            fontSize={"sm"}
            fontWeight={500}
            color={
              router.pathname.startsWith(navItem.href || "#")
                ? activeLinkColor
                : linkColor
            }
            _hover={{
              textDecoration: "none",
              color: linkHoverColor,
            }}
          >
            {navItem.label}
          </Link>
        </PopoverTrigger>

        {navItem.children && (
          <PopoverContent
            border={0}
            boxShadow={"xl"}
            bg={popoverContentBgColor}
            p={4}
            rounded={"xl"}
            minW={"sm"}
          >
            <Stack>
              {navItem.children.map((child) => (
                <DesktopSubNav key={child.label} {...child} />
              ))}
            </Stack>
          </PopoverContent>
        )}
      </Popover>
    </Box>
  );
};

const DesktopNav = () => {
  const { isAuthenticated } = useAuthentication();
  const { data: getUserData } = useGetUser(isAuthenticated);

  const isAdmin = getUserData?.isAdminUser;

  const adminRoute = {
    label: "Admin",
    href: "/admin",
  };

  let combinedNavItems = NAV_ITEMS;

  if (isAdmin) {
    combinedNavItems = [...NAV_ITEMS, adminRoute];
  }

  return (
    <Stack direction={"row"} spacing={4}>
      {combinedNavItems.map((navItem) => (
        <DesktopNavComponent key={navItem.label} navItem={navItem} />
      ))}
      {NAV_ITEMS_DESKTOP.map((navItem) => (
        <DesktopNavComponent key={navItem.label} navItem={navItem} />
      ))}
    </Stack>
  );
};

const DesktopSubNav = ({ label, href, subLabel }: NavItem) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { deleteUserMutate } = useDeleteUser({});

  const router = useRouter();
  const queryClient = useQueryClient();

  const handleCancelDelete = () => console.log("cancel");

  const handleSubmitDelete = () => {
    deleteUserMutate();
  };

  const doLogout = async () => {
    await firebaseLogout();
    await new Promise((r) => setTimeout(r, 200));
    // queryClient.resetQueries();
    // queryClient.clear();
    queryClient.removeQueries();

    await router.push("/");
  };

  return (
    <Link
      href={href}
      role={"group"}
      display={"block"}
      onClick={async () => {
        localStorage.removeItem("redirectSlug");
        if (label == "Delete Account") {
          onOpen();
        } else if (label == "Sign Out") {
          await doLogout();
        }
      }}
      p={2}
      rounded={"md"}
      _hover={{ bg: useColorModeValue("pink.50", "gray.900") }}
    >
      <DeleteAlertDialog
        entityTypeToDelete="Account"
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        onCancel={handleCancelDelete}
        onSubmit={handleSubmitDelete}
      />
      <Stack direction={"row"} align={"center"}>
        <Box>
          <Text
            transition={"all .3s ease"}
            _groupHover={{ color: "pink.400" }}
            fontWeight={500}
          >
            {label}
          </Text>
          <Text fontSize={"sm"}>{subLabel}</Text>
        </Box>
        <Flex
          transition={"all .3s ease"}
          transform={"translateX(-10px)"}
          opacity={0}
          _groupHover={{ opacity: "100%", transform: "translateX(0)" }}
          justify={"flex-end"}
          align={"center"}
          flex={1}
        >
          <Icon color={"pink.400"} w={5} h={5} as={ChevronRightIcon} />
        </Flex>
      </Stack>
    </Link>
  );
};

type MobileNavProps = {
  onToggle: () => void;
};
const MobileNav = ({ onToggle }: MobileNavProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isAuthenticated } = useAuthentication();
  const { data: getUserData } = useGetUser(isAuthenticated);

  const isAdmin = getUserData?.isAdminUser;

  const { deleteUserMutate } = useDeleteUser({});
  const router = useRouter();
  const queryClient = useQueryClient();

  const doLogout = async () => {
    await firebaseLogout();
    await new Promise((r) => setTimeout(r, 200));
    // queryClient.resetQueries();
    // queryClient.clear();
    queryClient.removeQueries();

    await router.push("/");
  };

  const doDelete = async () => {
    onOpen();
  };

  const handleCancelDelete = () => console.log("cancel");

  const handleSubmitDelete = () => {
    deleteUserMutate();
  };

  const adminRoute = {
    label: "Admin",
    href: "/admin",
  };

  let combinedNavItems = NAV_ITEMS;

  if (isAdmin) {
    combinedNavItems = [...NAV_ITEMS, adminRoute];
  }

  return (
    <Stack
      bg={useColorModeValue("white", "gray.800")}
      p={4}
      display={{ lg: "none" }}
    >
      <DeleteAlertDialog
        entityTypeToDelete="Account"
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        onCancel={handleCancelDelete}
        onSubmit={handleSubmitDelete}
      />
      {combinedNavItems.map((navItem) => (
        <MobileNavItem onClick={onToggle} key={navItem.label} {...navItem} />
      ))}
      <MobileNavItem
        onClick={async () => {
          onToggle;
          await doLogout();
        }}
        key={"signout"}
        label="Sign out"
      />
      <MobileNavItem
        onClick={async () => {
          onToggle;
          await doDelete();
        }}
        key={"deleteAccount"}
        label="Delete Account"
      />
    </Stack>
  );
};

const MobileNavItem = ({ label, children, href, onClick }: NavItem) => {
  const { isOpen, onToggle } = useDisclosure();
  const linkColor = useColorModeValue("gray.600", "gray.200");
  const activeLinkColor = useColorModeValue("brand.500", "brand.500");
  const router = useRouter();

  return (
    <Stack spacing={4} onClick={children && onToggle}>
      <Flex
        py={2}
        as={NextLink}
        href={href ?? "#"}
        onClick={onClick}
        justify={"space-between"}
        align={"center"}
        _hover={{
          textDecoration: "none",
        }}
      >
        <Text
          fontWeight={600}
          color={
            router.pathname.startsWith(href || "#")
              ? activeLinkColor
              : linkColor
          }
        >
          {label}
        </Text>
        {children && (
          <Icon
            as={ChevronDownIcon}
            transition={"all .25s ease-in-out"}
            transform={isOpen ? "rotate(180deg)" : ""}
            w={6}
            h={6}
          />
        )}
      </Flex>

      <Collapse in={isOpen} animateOpacity style={{ marginTop: "0!important" }}>
        <Stack
          mt={2}
          pl={4}
          borderLeft={1}
          borderStyle={"solid"}
          borderColor={useColorModeValue("gray.200", "gray.700")}
          align={"start"}
        >
          {children &&
            children.map((child) => (
              <Link key={child.label} py={2} href={child.href}>
                {child.label}
              </Link>
            ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

interface NavItem {
  label: string;
  subLabel?: string;
  children?: Array<NavItem>;
  href?: string;
  onClick?: () => void;
}

const NAV_ITEMS: Array<NavItem> = [
  {
    label: "Recipes",
    href: "/recipes",
  },
  {
    label: "Recipe Collections",
    href: "/collections",
  },
  {
    label: "Shopping Lists",
    href: "/shopping-lists",
  },
  {
    label: "Meal Planner",
    href: "/meal-planner",
  },
  {
    label: "Feedback",
    href: "/support",
  },
];

const NAV_ITEMS_DESKTOP: Array<NavItem> = [
  {
    label: "Settings",
    children: [
      {
        label: "Sign Out",
      },
      {
        label: "Delete Account",
      },
    ],
  },
];
